import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import {
  createPlatformTicket,
  fetchBillingAccounts,
  fetchPlatformTicket,
  fetchPlatformTickets,
  replyPlatformTicket,
  sendPaymentLink,
  toggleBusiness,
  updateTicketStatus,
} from './api';
import type { BillingAccount, PlatformTicket } from './types';
import { fmtDate, statusStyle } from './utils';

const BillingSupportPage: React.FC = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialBiz = searchParams.get('business_id');
  const urgentOnly = searchParams.get('urgent') === '1';
  const [accounts, setAccounts] = useState<BillingAccount[]>([]);
  const [tickets, setTickets] = useState<PlatformTicket[]>([]);
  const [selectedBiz, setSelectedBiz] = useState<number | null>(
    initialBiz ? parseInt(initialBiz, 10) : null
  );
  const [selectedTicket, setSelectedTicket] = useState<PlatformTicket | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'issues'>('issues');
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [billing, support] = await Promise.all([
        fetchBillingAccounts(token),
        fetchPlatformTickets(token, { status: 'all' }),
      ]);
      setAccounts(billing);
      setTickets(support);
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const filteredAccounts = useMemo(() => {
    const q = search.toLowerCase();
    return accounts.filter((a) => {
      const matchSearch = !q ||
        a.name.toLowerCase().includes(q) ||
        (a.owner_email || '').toLowerCase().includes(q);
      const matchFilter = filter === 'all' || ['past_due', 'unpaid', 'canceled'].includes(a.subscription_status || '');
      return matchSearch && matchFilter;
    });
  }, [accounts, search, filter]);

  const visibleTickets = useMemo(() => {
    let list = tickets;
    if (selectedBiz) list = list.filter((t) => t.business_id === selectedBiz);
    if (urgentOnly) list = list.filter((t) => t.priority === 'urgent');
    return list;
  }, [tickets, selectedBiz, urgentOnly]);

  const selectBusiness = (id: number) => {
    setSelectedBiz((prev) => (prev === id ? null : id));
    setSelectedTicket(null);
  };

  const openTicket = async (t: PlatformTicket) => {
    if (!token) return;
    try {
      setSelectedTicket(await fetchPlatformTicket(token, t.id));
    } catch {
      setSelectedTicket(t);
    }
  };

  const sendReply = async () => {
    if (!token || !selectedTicket || !reply.trim()) return;
    setBusy(true);
    try {
      await replyPlatformTicket(token, selectedTicket.id, reply.trim());
      setReply('');
      setSelectedTicket(await fetchPlatformTicket(token, selectedTicket.id));
      toast.success('Reply sent');
      await load();
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setBusy(false);
    }
  };

  const resolveTicket = async (status: string) => {
    if (!token || !selectedTicket) return;
    try {
      await updateTicketStatus(token, selectedTicket.id, status);
      setSelectedTicket(await fetchPlatformTicket(token, selectedTicket.id));
      toast.success(`Ticket ${status}`);
      await load();
    } catch {
      toast.error('Failed to update ticket');
    }
  };

  const openBillingCase = async (account: BillingAccount) => {
    if (!token) return;
    const subject = newTicket.subject.trim() || `Billing follow-up — ${account.name}`;
    const message = newTicket.message.trim() ||
      `Platform review for ${account.name} (${account.subscription_status || 'unknown status'}). Owner: ${account.owner_email || 'n/a'}.`;
    setBusy(true);
    try {
      await createPlatformTicket(token, {
        business_id: account.id,
        subject,
        message,
        priority: ['past_due', 'unpaid'].includes(account.subscription_status || '') ? 'high' : 'medium',
        category: 'billing',
      });
      toast.success('Billing support case opened');
      setNewTicket({ subject: '', message: '' });
      setSelectedBiz(account.id);
      await load();
    } catch {
      toast.error('Failed to open case');
    } finally {
      setBusy(false);
    }
  };

  const suspendAccount = async (account: BillingAccount) => {
    if (!window.confirm(`${account.subscription_status === 'active' ? 'Suspend' : 'Reactivate'} ${account.name}?`)) return;
    setBusy(true);
    try {
      await toggleBusiness(token!, account.id);
      toast.success('Account updated');
      await load();
    } catch {
      toast.error('Failed to update account');
    } finally {
      setBusy(false);
    }
  };

  const resendPayment = async (account: BillingAccount) => {
    if (!account.registration_id) {
      toast.error('No linked registration for payment link');
      return;
    }
    setBusy(true);
    try {
      await sendPaymentLink(token!, account.registration_id);
      toast.success('Payment link sent');
    } catch {
      toast.error('Failed to send payment link');
    } finally {
      setBusy(false);
    }
  };

  const selectedAccount = selectedBiz ? accounts.find((a) => a.id === selectedBiz) : null;

  return (
    <>
      <div className="plat-header">
        <div>
          <h2>Billing &amp; support</h2>
          <p>Subscription status linked to billing and account support tickets.</p>
        </div>
        <button type="button" className="plat-btn plat-btn-ghost" onClick={load}>Refresh</button>
      </div>

      <div className="plat-split">
        <div>
          <div className="plat-toolbar">
            <input
              className="plat-search"
              placeholder="Search business or owner email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="plat-pills">
              <button type="button" className={`plat-pill${filter === 'issues' ? ' active' : ''}`} onClick={() => setFilter('issues')}>Issues only</button>
              <button type="button" className={`plat-pill${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All accounts</button>
            </div>
          </div>

          <div className="plat-panel" style={{ padding: 0, overflow: 'hidden' }}>
            {loading ? (
              <div className="plat-empty">Loading…</div>
            ) : filteredAccounts.length === 0 ? (
              <div className="plat-empty">No billing accounts found.</div>
            ) : (
              <div className="plat-table-wrap">
                <table className="plat-table">
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Owner</th>
                      <th>Plan / status</th>
                      <th>Trial / period</th>
                      <th>Tickets</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccounts.map((a) => (
                      <tr
                        key={a.id}
                        className={selectedBiz === a.id ? 'selected' : ''}
                        onClick={() => selectBusiness(a.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div style={{ fontWeight: 700 }}>{a.name}</div>
                          <div className="plat-muted">{a.business_type || '—'}</div>
                        </td>
                        <td>
                          <div>{a.owner_name || '—'}</div>
                          <div className="plat-muted">{a.owner_email || '—'}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{a.plan || a.registration_plan || '—'}</div>
                          <span style={statusStyle(a.subscription_status || 'active')}>{a.subscription_status || 'active'}</span>
                          {a.payment_status && <div className="plat-muted">Pay: {a.payment_status}</div>}
                        </td>
                        <td>
                          {a.trial_ends_at ? <>Trial {fmtDate(a.trial_ends_at)}</> : fmtDate(a.current_period_end)}
                          {a.deletion_scheduled_at && (
                            <div className="plat-muted" style={{ color: '#f87171' }}>GDPR {fmtDate(a.deletion_scheduled_at)}</div>
                          )}
                        </td>
                        <td>
                          {a.open_billing_tickets > 0 ? (
                            <span className="plat-badge">{a.open_billing_tickets}</span>
                          ) : (
                            <span className="plat-muted">0</span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={busy} onClick={() => openBillingCase(a)}>
                              Open case
                            </button>
                            {a.registration_id && (
                              <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" disabled={busy} onClick={() => resendPayment(a)}>
                                Pay link
                              </button>
                            )}
                            <button type="button" className="plat-btn plat-btn-danger plat-btn-sm" disabled={busy} onClick={() => suspendAccount(a)}>
                              {a.subscription_status === 'active' ? 'Suspend' : 'Restore'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {selectedAccount && (
            <div className="plat-panel" style={{ marginTop: 14 }}>
              <h3>Open billing case — {selectedAccount.name}</h3>
              <input
                className="plat-search"
                style={{ maxWidth: '100%', marginBottom: 8 }}
                placeholder="Subject (optional)"
                value={newTicket.subject}
                onChange={(e) => setNewTicket((s) => ({ ...s, subject: e.target.value }))}
              />
              <textarea
                className="plat-textarea"
                placeholder="Reason / notes for support case…"
                value={newTicket.message}
                onChange={(e) => setNewTicket((s) => ({ ...s, message: e.target.value }))}
              />
              <button type="button" className="plat-btn plat-btn-primary" style={{ marginTop: 8 }} disabled={busy} onClick={() => openBillingCase(selectedAccount)}>
                Create billing ticket
              </button>
            </div>
          )}
        </div>

        <div>
          <div className="plat-panel">
            <h3>
              Support tickets
              {selectedBiz ? ` — ${selectedAccount?.name || ''}` : ' — all billing/account'}
            </h3>
            {visibleTickets.length === 0 ? (
              <div className="plat-muted">No open billing or account tickets.</div>
            ) : (
              visibleTickets.slice(0, 12).map((t) => (
                <div
                  key={t.id}
                  className={`plat-ticket-card${selectedTicket?.id === t.id ? ' active' : ''}`}
                  onClick={() => openTicket(t)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <strong style={{ fontSize: 13 }}>{t.subject}</strong>
                    <span style={statusStyle(t.priority)}>{t.priority}</span>
                  </div>
                  <div className="plat-muted" style={{ marginTop: 4 }}>{t.business_name} · {t.status}</div>
                </div>
              ))
            )}
          </div>

          {selectedTicket && (
            <div className="plat-detail">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <strong>{selectedTicket.subject}</strong>
                <span style={statusStyle(selectedTicket.status)}>{selectedTicket.status}</span>
              </div>
              <div className="plat-muted" style={{ marginBottom: 12 }}>
                {selectedTicket.business_name} · {selectedTicket.submitter_email}
              </div>
              <div className="plat-msg">{selectedTicket.message}</div>
              {(selectedTicket.responses || []).map((r) => (
                <div key={r.id} className={`plat-msg${r.message.includes('[Platform ops') ? ' ops' : ''}`}>
                  <div className="plat-muted" style={{ marginBottom: 4 }}>{r.responder_name || 'User'} · {fmtDate(r.createdAt)}</div>
                  {r.message}
                </div>
              ))}
              <textarea
                className="plat-textarea"
                placeholder="Reply as platform ops…"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button type="button" className="plat-btn plat-btn-primary plat-btn-sm" disabled={busy || !reply.trim()} onClick={sendReply}>Send</button>
                <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={() => resolveTicket('resolved')}>Resolve</button>
                <button type="button" className="plat-btn plat-btn-ghost plat-btn-sm" onClick={() => resolveTicket('closed')}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BillingSupportPage;
